import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { usePrets, useStructures } from '@/api/hooks';
import { LIBELLE_ETAPE, LIBELLE_STATUT_PRET, type StatutPret } from '@/api/types';
import { accedeModule } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { montantDevise, StatutPretBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dateFr } from '@/lib/utils';

const STATUTS = Object.keys(LIBELLE_STATUT_PRET) as StatutPret[];

/** Prêts sociaux : à traiter (validateurs, instructeur INV), plafonds à vérifier (RH), portefeuille (RH, INV, DF, DGAF, DG) et mes demandes. */
export function PretsListe() {
  const u = useSession()!.utilisateur;
  const pro = accedeModule(u.profils, 'prets');
  const rh = u.profils.some((p) => p === 'DIRECTEUR_RH' || p === 'RESSOURCES_HUMAINES');
  const onglets = pro ? (rh ? (['a-traiter', 'plafonds', 'portefeuille', 'mes'] as const) : (['a-traiter', 'portefeuille', 'mes'] as const)) : (['mes'] as const);
  type Onglet = (typeof onglets)[number];
  const [params, setParams] = useSearchParams();
  const o = params.get('onglet');
  const onglet: Onglet = o && (onglets as readonly string[]).includes(o) ? (o as Onglet) : onglets[0];
  const setOnglet = (v: Onglet) => setParams(v === onglets[0] ? {} : { onglet: v }, { replace: true });
  const [statut, setStatut] = useState<StatutPret | 'TOUS'>('TOUS');
  const [structure, setStructure] = useState('');
  const structures = useStructures();
  const { data, isLoading, error } = usePrets({ onglet, statut: onglet === 'portefeuille' && statut !== 'TOUS' ? statut : undefined, structure: onglet === 'portefeuille' && structure ? structure : undefined });
  return (
    <div>
      <PageHeader
        titre="Prêts sociaux"
        sousTitre={pro ? 'Dossiers à traiter, plafonds à vérifier, portefeuille des demandes et vos propres demandes.' : 'Vos demandes de prêt social.'}
        actions={
          <Button asChild>
            <Link to="/rh/prets/nouvelle">
              <Plus className="size-4" /> Demander un prêt
            </Link>
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {onglets.length > 1 && (
          <Tabs value={onglet} onValueChange={(v) => setOnglet(v as Onglet)}>
            <TabsList>
              {(onglets as readonly string[]).includes('a-traiter') && <TabsTrigger value="a-traiter">À traiter</TabsTrigger>}
              {(onglets as readonly string[]).includes('plafonds') && <TabsTrigger value="plafonds">Plafonds à vérifier</TabsTrigger>}
              {(onglets as readonly string[]).includes('portefeuille') && <TabsTrigger value="portefeuille">Portefeuille</TabsTrigger>}
              <TabsTrigger value="mes">Mes demandes</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        {onglet === 'portefeuille' && (
          <>
            <SelectNatif value={statut} onChange={(e) => setStatut(e.target.value as StatutPret | 'TOUS')}>
              <option value="TOUS">Tous les statuts</option>
              {STATUTS.map((s) => (
                <option key={s} value={s}>
                  {LIBELLE_STATUT_PRET[s]}
                </option>
              ))}
            </SelectNatif>
            <SelectNatif value={structure} onChange={(e) => setStructure(e.target.value)}>
              <option value="">Toutes les structures</option>
              {(structures.data ?? []).map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} · {s.libelle}
                </option>
              ))}
            </SelectNatif>
          </>
        )}
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message={onglet === 'a-traiter' ? 'Aucun dossier de prêt en attente de votre intervention.' : onglet === 'plafonds' ? 'Aucun plafond à vérifier.' : 'Aucune demande de prêt.'} />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead className="hidden md:table-cell">Nature</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="hidden sm:table-cell text-right">Durée · mensualité</TableHead>
                <TableHead className="hidden lg:table-cell">Déposée le</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>
                    <Link to={`/rh/prets/${d.id}`} className="font-medium text-primary hover:underline">
                      {d.numero ?? 'Demande'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {d.agent.nom}
                    <div className="text-xs text-muted-foreground">{d.structure.code}</div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{d.libelleNature}</TableCell>
                  <TableCell className="num text-right">
                    {montantDevise(d.montantConfirme ?? d.montantSouhaite, d.devise)}
                    {d.montantConfirme === null && <div className="text-xs text-muted-foreground">souhaité</div>}
                  </TableCell>
                  <TableCell className="num hidden sm:table-cell text-right">{d.dureeMois ? `${d.dureeMois} mois · ${montantDevise(d.mensualite ?? 0, d.devise)}` : '—'}</TableCell>
                  <TableCell className="hidden lg:table-cell">{dateFr(d.dateDepot)}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutPretBadge statut={d.statut} />
                    {d.statut === 'SOUMIS' && d.etapeCourante && <Badge variant="secondary">{d.conditionsEnAttente ? 'Conditions à accepter' : LIBELLE_ETAPE[d.etapeCourante]}</Badge>}
                    {d.plafond?.expire && !['APPROUVE', 'REJETE', 'ANNULE', 'SOUMIS'].includes(d.statut) && <Badge variant="destructive">plafond expiré</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
